/**
 * Read a number the way people type it in German or English notation.
 *
 * `1.000,50`, `1,000.50`, `-2500`, `1 234,5` and `0,75` each have one
 * reading. A single `.` or `,` followed by exactly three digits (`2.500`,
 * `12,345`) has two: thousands in one convention, a decimal in the other.
 * That reading follows the given locale and is reported as an ambiguity, so
 * the caller can say how it read the input instead of guessing silently.
 * Anything else, such as misplaced grouping (`1.00,5`) or letters, is invalid.
 */

export interface NumberSeparators {
  readonly group: string;
  readonly decimal: string;
}

export interface NumberAmbiguity {
  /** The separator that had two readings. */
  readonly separator: string;
  readonly readAs: 'group' | 'decimal';
  /** The locale whose convention decided. */
  readonly locale: string;
}

export type LocalizedNumberInput =
  | { readonly kind: 'empty' }
  | { readonly kind: 'invalid' }
  | { readonly kind: 'number'; readonly value: number; readonly ambiguity: NumberAmbiguity | null };

const CONVENTIONS: readonly NumberSeparators[] = [
  { group: ',', decimal: '.' },
  { group: '.', decimal: ',' },
];
// Whitespace-like grouping (no-break and narrow no-break spaces, apostrophes)
// and underscores never change a number's value.
const IGNORED_GROUPING_PATTERN = /[\s\u00A0\u202F'\u2019_]/gu;
const SEPARATORS_BY_LOCALE = new Map<string, NumberSeparators>();

/** Group and decimal separators the given locale renders with. */
export function separatorsForLocale(locale: string): NumberSeparators {
  const cached = SEPARATORS_BY_LOCALE.get(locale);
  if (cached !== undefined) return cached;
  let group = ',';
  let decimal = '.';
  // oxlint-disable-next-line react-doctor/js-hoist-intl -- runs once per locale; the result is cached above.
  for (const part of new Intl.NumberFormat(locale).formatToParts(1234567.89)) {
    if (part.type === 'group') group = part.value;
    else if (part.type === 'decimal') decimal = part.value;
  }
  const separators: NumberSeparators = { group, decimal };
  SEPARATORS_BY_LOCALE.set(locale, separators);
  return separators;
}

/**
 * A number in the locale's decimal notation without grouping (`2500`,
 * `1250,5`), which `parseLocalizedNumber` reads back unambiguously.
 */
export function formatPlainNumber(value: number, locale: string): string {
  return String(value).replace('.', separatorsForLocale(locale).decimal);
}

export function parseLocalizedNumber(raw: string, locale: string): LocalizedNumberInput {
  const text = raw.trim().replace(IGNORED_GROUPING_PATTERN, '').replace(/^\u2212/u, '-');
  if (text === '') return { kind: 'empty' };
  const readings = new Map<number, NumberSeparators>();
  for (const convention of CONVENTIONS) {
    const value = readWithConvention(text, convention);
    if (value !== null && !readings.has(value)) readings.set(value, convention);
  }
  const entries = [...readings.entries()];
  const [only] = entries;
  if (only === undefined) return { kind: 'invalid' };
  if (entries.length === 1) return { kind: 'number', value: only[0], ambiguity: null };
  const preferred = separatorsForLocale(locale);
  const chosen =
    entries.find(([, convention]) => convention.group === preferred.group) ??
    entries.find(([, convention]) => convention.decimal === preferred.decimal) ??
    only;
  const separator = text.includes('.') ? '.' : ',';
  return {
    kind: 'number',
    value: chosen[0],
    ambiguity: { separator, readAs: chosen[1].group === separator ? 'group' : 'decimal', locale },
  };
}

function readWithConvention(text: string, { group, decimal }: NumberSeparators): number | null {
  const g = escapeForPattern(group);
  const d = escapeForPattern(decimal);
  // Grouped integers start with 1–3 digits (not a lone leading zero) and
  // continue in groups of exactly three.
  const pattern = new RegExp(`^([+-]?)([1-9]\\d{0,2}(?:${g}\\d{3})+|\\d*)(?:${d}(\\d+))?$`, 'u');
  const match = pattern.exec(text);
  if (match === null) return null;
  const [, sign = '', integer = '', fraction] = match;
  if (integer === '' && fraction === undefined) return null;
  const value = Number(`${sign}${integer.split(group).join('') || '0'}.${fraction ?? '0'}`);
  return Number.isFinite(value) ? value : null;
}

function escapeForPattern(separator: string): string {
  return separator.replace(/[.*+?^${}()|[\]\\]/gu, '\\$&');
}
